import { Box, Stack, Divider } from "@mui/material";
import MooneLogo from "Assets/Images/Images/moone-logo.svg";
import { PageTitle } from "Components/Shared/PageTitles/PageTitle";
import { Resources, useResource } from "Translations/Resources";
import { type ReactNode, useEffect } from "react";

type Props = {
  title: string;
  children: ReactNode;
  onChevronClick?: () => void;
  secondaryLogo?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  /**
   * Stretches the layout to the full viewport height so children can use
   * flexGrow to pin content to the bottom of the screen.
   */
  isFullHeight?: boolean;
};

export const AnonymousLayout = ({
  title,
  children,
  onChevronClick,
  secondaryLogo,
  isFullHeight = false,
}: Props) => {
  const { t } = useResource();

  useEffect(() => {
    document.title = `${title} | ${t(Resources.Common.AppName)}`;
  }, [title, t]);

  return (
    <Box
      display="flex"
      overflow="hidden"
      minHeight={isFullHeight ? "100dvh" : undefined}
    >
      <Stack
        position="relative"
        width="100%"
        padding={({ spacing }) => [
          spacing(6, 3, 3, 3),
          spacing(6, 3),
          spacing(6, 12),
        ]}
        zIndex={2}
      >
        <PageTitle onChevronClick={onChevronClick}>
          <Box display="flex" alignItems="center" gap={2}>
            <img src={MooneLogo} alt="moone logo" width={143} height={45} />
            {secondaryLogo && (
              <>
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{
                    height: 45,
                    borderColor: "text.primary",
                    opacity: 0.3,
                  }}
                />
                <img
                  src={secondaryLogo.src}
                  alt={secondaryLogo.alt}
                  width={secondaryLogo.width || 45}
                  height={secondaryLogo.height || 45}
                  style={{ objectFit: "contain" }}
                />
              </>
            )}
          </Box>
        </PageTitle>
        <Box
          mt={[4, 6]}
          {...(isFullHeight
            ? { flexGrow: 1, display: "flex" }
            : { height: "100%" })}
        >
          {children}
        </Box>
      </Stack>
    </Box>
  );
};
